<script setup lang="ts">
import { $getChangeRequest } from '@/api/changeRequests'
import { useSnackbarStore } from '@/store/snackbar'
import type { ChangeRequest } from '@/models/changeRequest'

interface IProps {
  changeRequestId: number | string | null | undefined
  actionRequiredFilter?: boolean,
  showChangeRequestExternalLink?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  changeRequestId: null,
  actionRequiredFilter: false,
  showChangeRequestExternalLink: false,
})

interface CustomEvents {
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const route = useRoute()
const router = useRouter()

const isMounted = ref<boolean>(false)

const changeRequest = ref<ChangeRequest>({})
const changeRequestLoading = ref<boolean>(false)

const isShowCRInfo = computed(() => {
  // Check if the Action Required filter is active:
  // 1. If the Change Request (CR) has a valid status (Action Required), show CR Info
  // 2. If the Change Request only has two fields (ID and Code) and no valid status (No Action Required), hide CR Info
  if (props.actionRequiredFilter) {
    return !!changeRequest.value?.status
  }

  // If the Action Required filter is inactive, always show CR Info
  return true
})

const getChangeRequest = async () => {
  changeRequestLoading.value = true

  try {
    const res = await $getChangeRequest(String(props.changeRequestId), {
      action_required: props.actionRequiredFilter || undefined
    })
    changeRequest.value = res.data
  } catch (e: any) {
    console.log(e)
  } finally {
    changeRequestLoading.value = false
  }
}

watch(() => props.actionRequiredFilter, () => {
  getChangeRequest()
})

onMounted(() => {
  if (props.changeRequestId) {
    getChangeRequest()
  }
  isMounted.value = true
})

const fileAttachments = computed(() => changeRequest.value.file_attachments || [])
</script>

<template>
  <div>
    <VCard v-if="changeRequestLoading || changeRequest?.id">
      <template #title>
        <div class="d-flex align-center">
          Change Request
          {{ changeRequest.code }}
          <VBtn
            v-if="showChangeRequestExternalLink"
            size=""
            :disabled="!changeRequestId"
            class="ml-2 px-2 py-1"
            :to="{ path: `/change-requests/${changeRequestId}` }"
          >
            <VIcon icon="tabler-external-link" />
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                Open Full Change Request Page
              </span>
            </VMenu>
          </VBtn>
        </div>
      </template>
      <template #append>
        <VBtn
          v-if="isShowCRInfo"
          :disabled="!changeRequest?.id"
          :to="{ path: `/change-requests/${changeRequest?.id}/edit`, query: route.query }"
        >
          Edit
        </VBtn>
      </template>
      <VCardText>
        <VOverlay
          v-model="changeRequestLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <div v-if="isShowCRInfo">
          <VRow>
            <VCol
              cols="12"
              class="d-flex align-center justify-space-between"
            >
              <h3>
                {{ changeRequest.name }}
              </h3>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Change Request Type: </label>
              <div>
                {{ changeRequest.request_type_display }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Type of Project: </label>
              <div>
                {{ changeRequest.project_type_display }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Project: </label>
              <div>
                {{ changeRequest.project_name }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Item Code (if known):</label>
              <div>
                {{ changeRequest.item_code }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Services Required:</label>
              <div>
                {{ changeRequest?.services_required?.map(i => i.charAt(0).toUpperCase() + i.slice(1)).join(', ') }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Assigned To:</label>
              <div>
                {{ changeRequest.assigned_to_name }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Status: </label>
              <div>
                {{ changeRequest.status_display }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Details: </label>
              <div>
                {{ changeRequest.details }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <label class="label-text font-weight-bold">Client Reference: </label>
              <div>
                {{ changeRequest.client_reference }}
              </div>
            </VCol>
          </VRow>
          <VRow>
            <VCol
              cols="12"
            >
              <label class="label-text font-weight-bold">File Attachments: </label>
              <div v-if="changeRequestId" class="file-container mt-1">
                <div v-for="file in fileAttachments" :key="file.id" class="file-item">
                  <div class="file-icon">
                    <VIcon icon="tabler-file-description" size="40" />
                  </div>
                  <div class="action-buttons">
                    <VBtn
                      v-if="file.file"
                      size="sm"
                      class="pa-1"
                      :href="file.file"
                      target="_blank"
                    >
                      <VIcon size="">mdi-eye</VIcon>
                    </VBtn>
                  </div>
                </div>
              </div>
            </VCol>
          </VRow>
        </div>
      </VCardText>
    </VCard>
    <VCard v-else>
      <template #title>
        Change Request not found, or you do not have access to it
      </template>
    </VCard>
  </div>
</template>

<style lang="scss">
.file-container {
  position: relative;
}

.file-item {
  position: relative;
  display: inline-block;
  margin-right: 10px;

  &:hover {
    .action-buttons {
      display: flex;
    }
  }
}

.file-icon {
  display: inline-block;
  position: relative;
}

.action-buttons {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 4px;
}

.action-buttons v-btn {
  background: rgba(0, 0, 0, 0.3);
  color: white;
}
</style>
